import { Video } from '@remotion/media'
import { AbsoluteFill, useVideoConfig } from 'remotion'
import ZealousSvg from '../assets/ZealousSvg'
import { TimelineMedia } from '../components/TimelineMedia'
import { LayoutInfo, LayoutProps } from '../types'
import { defaultClipScene } from '../types/defaultClipScene'
import { cn } from '../utils/cn'
import { buildMediaTimeline, useMediaTimeline } from '../utils/mediaTimeline'
import { useOrientation } from '../utils/useOrientation'

export default function VideoLayout({
  clipScene,
  children,
  orientation,
  speaker,
  style,
}: LayoutProps) {
  const { fps } = useVideoConfig()
  const { p, s, l, lp, sp, sl } = useOrientation(orientation)

  const aspectRatio =
    orientation === 'landscape' ? 16 / 9 : orientation === 'square' ? 1 : 9 / 16
  // `ClipScene`'s pieces when it provides them; the same computation if this
  // layout is ever mounted on its own.
  const pieces =
    useMediaTimeline() ??
    buildMediaTimeline({
      mediaStart: clipScene.mediaStart,
      duration: clipScene.duration,
      segments: clipScene.segments,
      fps,
    })

  return (
    <AbsoluteFill className="">
      {clipScene.showZealousLogo && (
        <ZealousSvg
          className={cn('absolute top-[60px] right-[60px] h-[49px] opacity-50')}
          color={style.logo.color}
        />
      )}

      <AbsoluteFill>
        <TimelineMedia pieces={pieces}>
          {(piece) => (
            <Video
              className="scale-110 overflow-hidden blur-md"
              // disallowFallbackToOffthreadVideo
              muted
              src={clipScene.mediaUrl}
              style={{ aspectRatio, objectFit: 'cover' }}
              trimAfter={piece.trimAfter}
              trimBefore={piece.trimBefore}
            />
          )}
        </TimelineMedia>
      </AbsoluteFill>

      <AbsoluteFill>
        <TimelineMedia pieces={pieces}>
          {(piece) => (
            <Video
              // disallowFallbackToOffthreadVideo
              muted
              src={clipScene.mediaUrl}
              style={{ aspectRatio, objectFit: 'contain' }}
              trimAfter={piece.trimAfter}
              trimBefore={piece.trimBefore}
            />
          )}
        </TimelineMedia>
      </AbsoluteFill>

      <div
        className={cn(
          'absolute bottom-0 w-full',
          p && 'px-[120px] pb-[400px]',
          sl && 'p-[60px]',
        )}
      >
        {children}
      </div>
    </AbsoluteFill>
  )
}

export const videoLayoutInfo: LayoutInfo = {
  name: 'video',
  Component: VideoLayout,
  defaultScene: {
    ...defaultClipScene,
    duration: 6,
    layout: 'meme',
    mediaUrl:
      'https://test-videos.co.uk/vids/bigbuckbunny/mp4/h264/1080/Big_Buck_Bunny_1080_10s_1MB.mp4',
    paragraphs: [{ start: 2.32, end: 5.7, speaker: { username: 'petrbela' } }],
    words: [
      { end: 2.82, start: 2.32, text: 'Okay.' },
      { end: 3.2, start: 2.88, text: "There's" },
      { end: 3.36, start: 3.2, text: 'a' },
      { end: 3.86, start: 3.36, text: 'test.' },
      { end: 5.2, start: 4.72, text: "Let's" },
      { end: 5.7, start: 5.2, text: 'see.' },
    ],
  },
  defaultStyle: {
    background: { color: 'primary' },
    border: { color: 'secondary' },
    logo: { color: 'secondary' },
    waveform: { color: 'accent' },
    transcription: {
      color: 'white',
      fontFamily: 'Impact',
      highlightColor: 'accent',
      unPlayedColor: 'white',
      unPlayedOpacity: 1,
      highlightType: 'last-word',
      fontSize: 100,
      scrollType: 'page-by-page',
      fontWeight: 700,
      linesPerPage: 1,
      lineHeight: 1.5,
      textAlign: 'center',
      textStrokeColor: 'black',
      textStrokeWidth: 30,
    },
    title: {
      color: 'secondary',
      fontFamily: 'primary',
    },
    subtitle: {
      color: 'secondary',
      fontFamily: 'primary',
      opacity: 0.5,
    },
    speakerName: {
      color: 'secondary',
      fontFamily: 'primary',
      opacity: 0.5,
    },
    speakerUsername: {
      color: 'secondary',
      fontFamily: 'primary',
      opacity: 0.5,
    },
  },
  transcriptions: {
    square: {},
    portrait: {
      linesPerPage: 1,
    },
    landscape: {
      linesPerPage: 1,
    },
  },
}
